import { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Loader2, Upload, X, FileText } from 'lucide-react';
import { toast } from 'sonner';
import API from '@/utils/api';

const insuranceTypes = [
  { value: 'medical', label: 'Medical / Health' },
  { value: 'dental', label: 'Dental' },
  { value: 'vision', label: 'Vision' },
  { value: 'auto', label: 'Auto' },
  { value: 'home', label: 'Home' },
  { value: 'renters', label: "Renter's" },
  { value: 'life', label: 'Life' },
  { value: 'disability', label: 'Disability' },
  { value: 'travel', label: 'Travel' },
  { value: 'other', label: 'Other' },
];

const documentTypes = {
  ids_travel: ['Passport', 'Driver License', 'State ID', 'Birth Certificate', 'Social Security Card', 'Visa', 'Travel Authorization', 'Global Entry', 'TSA PreCheck', 'Other'],
  insurance: ['Insurance Card', 'Policy Document', 'EOB', 'ID Card', 'Other'],
  car_registration: ['Registration', 'Title', 'Bill of Sale', 'Inspection', 'Emissions', 'Other'],
  licenses_certs: ['Driver License', 'Professional License', 'CPR Certification', 'First Aid', 'Food Handler', 'Teaching Certificate', 'Hunting License', 'Fishing License', 'Other'],
  home_utilities: ['Lease Agreement', 'Deed', 'Utility Bill', 'HOA Document', 'Warranty', 'Other'],
  work_school: ['Employment Contract', 'W2', 'Diploma', 'Transcript', 'Professional License', 'ID Badge', 'Other'],
  other: ['Other'],
};

const AddDocumentDialog = ({ open, onOpenChange, category, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    title: '',
    document_type: '',
    issuer: '',
    key_number: '',
    issue_date: '',
    expiration_date: '',
    status: 'active',
    notes: '',
    tags: '',
    
    // Insurance
    insurance_type: '',
    plan_name: '',
    group_number: '',
    coverage_notes: '',
    linked_person: '',
    
    // Car
    vin: '',
    plate_number: '',
    
    // License
    license_number: '',
    renewal_instructions: '',
    
    // Home
    account_number: '',
    service_address: '',
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setSelectedFile(file);
    
    // For images, do OCR extraction
    if (file.type.startsWith('image/')) {
      setExtracting(true);
      
      try {
        const formDataFile = new FormData();
        formDataFile.append('file', file);
        
        const response = await API.post('/documents/extract-ocr', formDataFile, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        if (response.data.success && response.data.data?.extracted) {
          const extracted = response.data.data.extracted;
          
          // Pre-fill form with extracted data
          setFormData(prev => ({
            ...prev,
            title: extracted.title || prev.title,
            document_type: extracted.document_type || prev.document_type,
            issuer: extracted.issuer || prev.issuer,
            key_number: extracted.key_number || prev.key_number,
            issue_date: extracted.issue_date || prev.issue_date,
            expiration_date: extracted.expiration_date || prev.expiration_date,
            notes: extracted.address ? `Address: ${extracted.address}` : prev.notes,
            // Insurance specific
            plan_name: extracted.plan_name || prev.plan_name,
            group_number: extracted.group_number || prev.group_number,
            // License specific
            license_number: extracted.key_number || prev.license_number,
          }));
          
          toast.success('Document info extracted! Please verify the details.');
        } else {
          toast.info('Could not extract info. Please fill in manually.');
        }
      } catch (error) {
        console.error('OCR extraction failed:', error);
        toast.info('OCR extraction failed. Please fill in manually.');
      } finally {
        setExtracting(false);
      }
    } else if (file.type === 'application/pdf') {
      toast.info('PDF selected. OCR will happen on save.');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      document_type: '',
      issuer: '',
      key_number: '',
      issue_date: '',
      expiration_date: '',
      status: 'active',
      notes: '',
      tags: '',
      insurance_type: '',
      plan_name: '',
      group_number: '',
      coverage_notes: '',
      linked_person: '',
      vin: '',
      plate_number: '',
      license_number: '',
      renewal_instructions: '',
      account_number: '',
      service_address: '',
    });
    setSelectedFile(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title) {
      toast.error('Please enter a document title');
      return;
    }
    
    setLoading(true);
    try {
      // Create document
      const dataToSend = {
        category_tab: category,
        title: formData.title,
        document_type: formData.document_type || null,
        issuer: formData.issuer || null,
        key_number: formData.key_number || null,
        issue_date: formData.issue_date || null,
        expiration_date: formData.expiration_date || null,
        status: formData.status,
        notes: formData.notes || null,
        tags: formData.tags ? formData.tags.split(',').map(t => t.trim()) : [],
        reminder_days: [30, 7],
      };
      
      // Add category-specific fields
      if (category === 'insurance') {
        dataToSend.insurance_type = formData.insurance_type || null;
        dataToSend.plan_name = formData.plan_name || null;
        dataToSend.group_number = formData.group_number || null;
        dataToSend.coverage_notes = formData.coverage_notes || null;
        dataToSend.linked_person = formData.linked_person || null;
      }
      
      if (category === 'car_registration') {
        dataToSend.vin = formData.vin || null;
        dataToSend.plate_number = formData.plate_number || null;
      }
      
      if (category === 'licenses_certs') {
        dataToSend.license_number = formData.license_number || null;
        dataToSend.renewal_instructions = formData.renewal_instructions || null;
      }
      
      if (category === 'home_utilities') {
        dataToSend.account_number = formData.account_number || null;
        dataToSend.service_address = formData.service_address || null;
      }
      
      const response = await API.post('/documents', dataToSend);
      const docId = response.data.id;
      
      // Upload file if selected
      if (selectedFile) {
        const formDataFile = new FormData();
        formDataFile.append('file', selectedFile);
        
        try {
          await API.post(`/documents/${docId}/upload`, formDataFile, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          toast.success('Document created with file attachment');
        } catch (uploadErr) {
          toast.warning('Document created but file upload failed');
        }
      } else {
        toast.success('Document created successfully');
      }
      
      resetForm();
      onSuccess();
    } catch (error) {
      console.error('Failed to create document:', error);
      toast.error('Failed to create document');
    } finally {
      setLoading(false);
    }
  };

  const types = documentTypes[category] || documentTypes.other;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[550px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add Document</DialogTitle>
          <DialogDescription>
            Upload or manually enter document details
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* File Upload */}
          <div className="border-2 border-dashed border-gray-200 rounded-lg p-4 text-center">
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileSelect}
              accept="image/*,.pdf"
              className="hidden"
            />
            {selectedFile ? (
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <FileText className="h-8 w-8 text-[#E0DCD3]" />
                  <div className="text-left">
                    <p className="font-medium text-sm">{selectedFile.name}</p>
                    <p className="text-xs text-gray-500">{(selectedFile.size / 1024).toFixed(1)} KB</p>
                  </div>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setSelectedFile(null)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <div>
                <Upload className="h-8 w-8 mx-auto text-gray-400 mb-2" />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={extracting}
                >
                  {extracting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Extracting...
                    </>
                  ) : (
                    <>
                      <Upload className="h-4 w-4 mr-2" />
                      Upload Photo or PDF
                    </>
                  )}
                </Button>
                <p className="text-xs text-gray-500 mt-2">OCR extraction will pre-fill fields</p>
              </div>
            )}
          </div>
          
          {/* Common Fields */}
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2 space-y-2">
              <Label htmlFor="title">Document Name *</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => handleChange('title', e.target.value)}
                placeholder="My Passport"
                data-testid="input-doc-title"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="document_type">Document Type</Label>
              <Select value={formData.document_type} onValueChange={(v) => handleChange('document_type', v)}>
                <SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger>
                <SelectContent>
                  {types.map(t => (
                    <SelectItem key={t} value={t}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="issuer">Issuer / Organization</Label>
              <Input
                id="issuer"
                value={formData.issuer}
                onChange={(e) => handleChange('issuer', e.target.value)}
                placeholder="US Dept of State"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="key_number">ID / Number</Label>
              <Input
                id="key_number"
                value={formData.key_number}
                onChange={(e) => handleChange('key_number', e.target.value)}
                placeholder="Will be masked"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="status">Status</Label>
              <Select value={formData.status} onValueChange={(v) => handleChange('status', v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="expired">Expired</SelectItem>
                  <SelectItem value="replaced">Replaced</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="issue_date">Issue Date</Label>
              <Input
                id="issue_date"
                type="date"
                value={formData.issue_date}
                onChange={(e) => handleChange('issue_date', e.target.value)}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="expiration_date">Expiration Date</Label>
              <Input
                id="expiration_date"
                type="date"
                value={formData.expiration_date}
                onChange={(e) => handleChange('expiration_date', e.target.value)}
              />
            </div>
          </div>
          
          {/* Insurance specific */}
          {category === 'insurance' && (
            <div className="border-t pt-4 space-y-4">
              <h4 className="text-sm font-medium text-gray-700">Insurance Details</h4>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="insurance_type">Coverage Type</Label>
                  <Select value={formData.insurance_type} onValueChange={(v) => handleChange('insurance_type', v)}>
                    <SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger>
                    <SelectContent>
                      {insuranceTypes.map(t => (
                        <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="plan_name">Plan Name</Label>
                  <Input
                    id="plan_name"
                    value={formData.plan_name}
                    onChange={(e) => handleChange('plan_name', e.target.value)}
                    placeholder="PPO Gold"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="group_number">Group Number</Label>
                  <Input
                    id="group_number"
                    value={formData.group_number}
                    onChange={(e) => handleChange('group_number', e.target.value)}
                    placeholder="Will be masked"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="linked_person">Linked Person</Label>
                  <Input
                    id="linked_person"
                    value={formData.linked_person}
                    onChange={(e) => handleChange('linked_person', e.target.value)}
                    placeholder="Primary holder name"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="coverage_notes">Coverage Notes</Label>
                <Textarea
                  id="coverage_notes"
                  value={formData.coverage_notes}
                  onChange={(e) => handleChange('coverage_notes', e.target.value)}
                  placeholder="Coverage details..."
                  rows={2}
                />
              </div>
            </div>
          )}
          
          {/* Car specific */}
          {category === 'car_registration' && (
            <div className="border-t pt-4 space-y-4">
              <h4 className="text-sm font-medium text-gray-700">Vehicle Details</h4>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="vin">VIN</Label>
                  <Input
                    id="vin"
                    value={formData.vin}
                    onChange={(e) => handleChange('vin', e.target.value)}
                    placeholder="Vehicle Identification Number"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="plate_number">Plate Number</Label>
                  <Input
                    id="plate_number"
                    value={formData.plate_number}
                    onChange={(e) => handleChange('plate_number', e.target.value)}
                    placeholder="ABC 1234"
                  />
                </div>
              </div>
            </div>
          )}
          
          {/* License specific */}
          {category === 'licenses_certs' && (
            <div className="border-t pt-4 space-y-4">
              <h4 className="text-sm font-medium text-gray-700">License Details</h4>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="license_number">License Number</Label>
                  <Input
                    id="license_number"
                    value={formData.license_number}
                    onChange={(e) => handleChange('license_number', e.target.value)}
                    placeholder="Will be masked"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="renewal_instructions">Renewal Instructions</Label>
                <Textarea
                  id="renewal_instructions"
                  value={formData.renewal_instructions}
                  onChange={(e) => handleChange('renewal_instructions', e.target.value)}
                  placeholder="How to renew..."
                  rows={2}
                />
              </div>
            </div>
          )}
          
          {/* Home specific */}
          {category === 'home_utilities' && (
            <div className="border-t pt-4 space-y-4">
              <h4 className="text-sm font-medium text-gray-700">Account Details</h4>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="account_number">Account Number</Label>
                  <Input
                    id="account_number"
                    value={formData.account_number}
                    onChange={(e) => handleChange('account_number', e.target.value)}
                    placeholder="Will be masked"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="service_address">Service Address</Label>
                  <Input
                    id="service_address"
                    value={formData.service_address}
                    onChange={(e) => handleChange('service_address', e.target.value)}
                    placeholder="123 Main St"
                  />
                </div>
              </div>
            </div>
          )}
          
          {/* Tags and Notes */}
          <div className="border-t pt-4 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="tags">Tags (comma separated)</Label>
              <Input
                id="tags"
                value={formData.tags}
                onChange={(e) => handleChange('tags', e.target.value)}
                placeholder="travel, important, 2024"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => handleChange('notes', e.target.value)}
                placeholder="Additional notes..."
                rows={2}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={loading}
              className="bg-[#E0DCD3] hover:bg-[#D5D0C7] text-gray-900"
              data-testid="submit-doc-btn"
            >
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save Document
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddDocumentDialog;
